/**
 * GymTimezoneSelect — dropdown that lets the owner change the gym
 * timezone. Selection is persisted to `site_settings.timezone` and is
 * used by every server-side cutoff (cancel/reschedule windows, no-show
 * ticks, reminder cron) plus the FE countdown label.
 *
 * Sachin 2026-03-05: "On the schedule, please make sure the timezone
 * is Pacific. Maybe make a drop down choosing the timezone to ensure
 * it's correct. Default to pacific."
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GymTimezoneSelect() {
    const [current, setCurrent] = useState(null);
    const [options, setOptions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/settings/timezone`, { withCredentials: true });
                setCurrent(r.data?.timezone || "America/Los_Angeles");
                setOptions(r.data?.options || []);
            } catch (e) {
                toast.error("Couldn't load timezone", { description: e?.response?.data?.detail });
            }
        })();
    }, []);

    const save = async (val) => {
        setSaving(true);
        try {
            await axios.put(
                `${API}/coach/settings/timezone`,
                { timezone: val },
                { withCredentials: true },
            );
            setCurrent(val);
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 2000);
            toast.success("Gym timezone updated", {
                description: "Every cutoff, reminder and countdown now uses this timezone.",
            });
        } catch (e) {
            toast.error("Couldn't save timezone", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    if (current === null) return null;

    return (
        <div
            data-testid="gym-timezone-select"
            className="border border-zinc-800 bg-zinc-950/60 rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
        >
            <div className="flex items-center gap-2 flex-1 min-w-0">
                <Clock className="w-4 h-4 text-brand-red flex-shrink-0" />
                <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Gym timezone</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 leading-tight">
                        Controls cancel windows, reminders, and every countdown parents see.
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                <select
                    data-testid="gym-timezone-dropdown"
                    value={current}
                    onChange={(e) => save(e.target.value)}
                    disabled={saving}
                    className="bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red disabled:opacity-60"
                >
                    {options.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                {saving ? (
                    <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" />
                ) : savedFlash ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                ) : null}
            </div>
        </div>
    );
}
